/**
 * 角色权限工具
 *
 * 权限数据来自 utils/businessRoutes.js（业务系统页面清单）
 *
 * 勾选时用「路径」操作（el-tree 的 node-key 就是 path）
 * 保存 / 下发的 permission 结构与 businessRoutes.js 完全一致：
 *
 *   选中模块   先输出 { path, title, children:[该模块全部页面] }
 *              再把该模块每个页面依次扁平输出 { path, title }
 *   选中单页面 只输出 { path, title }
 *   独立页面   只输出 { path, title }（如 /dashboard）
 *
 * 即：模块节点与其扁平副本同时存在，这是既定格式，不是冗余。
 */

import { businessPermissionTree as FLAT } from '././businessRouter';

/**
 * 转成 el-tree 用的纯树（去掉扁平副本，避免同一 path 出现两次导致 node-key 冲突）
 * 节点补 level：module（模块）/ page（页面）
 */
export function toTree() {
  const modules = FLAT.filter(n => n.children && n.children.length);
  const inModule = new Set();
  modules.forEach(m => m.children.forEach(c => inModule.add(c.path)));
  // 不属于任何模块的独立页面（如 /dashboard）
  const loose = FLAT.filter(n => !n.children && !inModule.has(n.path));

  const decorate = list => list.map(n => ({
    path: n.path,
    title: n.title,
    level: n.children && n.children.length ? 'module' : 'page',
    children: n.children && n.children.length ? decorate(n.children) : []
  }));

  return decorate([...modules, ...loose]);
}

/** 全部可授权路径（模块 + 页面） */
export function allPaths() {
  const set = new Set();
  const walk = nodes => (nodes || []).forEach(n => {
    set.add(n.path);
    if (n.children && n.children.length) walk(n.children);
  });
  walk(toTree());
  return Array.from(set);
}

/** 全部叶子页面路径 */
export function leafPaths() {
  const out = [];
  const walk = nodes => (nodes || []).forEach(n => {
    if (n.children && n.children.length) walk(n.children);
    else out.push(n.path);
  });
  walk(toTree());
  return out;
}

/** 取祖先路径：'/a/b/c' → ['/a', '/a/b'] */
export function ancestorsOf(path) {
  const parts = String(path).replace(/^\//, '').split('/');
  const out = [];
  for (let i = 0; i < parts.length - 1; i++) {
    out.push('/' + parts.slice(0, i + 1).join('/'));
  }
  return out;
}

/**
 * 勾选路径集合 -> 保存用的 permission（完整树格式）
 *
 * 按树顺序输出：
 *   选中模块 => 模块节点（带全部 children）+ 该模块每个页面依次扁平输出
 *   未选模块但选了其下页面 => 只输出这些页面
 *   选中独立页面 => 输出该页面
 */
export function buildPermission(keys) {
  const valid = allPaths();
  const raw = Array.from(new Set((keys || []).filter(Boolean)))
    .filter(p => valid.indexOf(p) !== -1);
  // 父级覆盖子级：若同时勾了模块与其下页面，只保留模块
  const picked = raw.filter(p => !ancestorsOf(p).some(a => raw.indexOf(a) !== -1));

  const out = [];

  // 全部模块路径，用于判断某个扁平项是否隶属于某模块
  const modulePaths = FLAT.filter(n => n.children && n.children.length).map(n => n.path);
  const ownerOf = path => modulePaths.find(m => path.indexOf(m + '/') === 0);

  // 严格按 businessRoutes.js 的原始顺序输出，保证与源数据结构一致
  FLAT.forEach(item => {
    if (item.children && item.children.length) {
      // 模块：选中则输出模块节点 + 其下每个页面依次扁平输出
      if (picked.indexOf(item.path) !== -1) {
        out.push({
          path: item.path,
          title: item.title,
          children: item.children.map(c => ({ path: c.path, title: c.title }))
        });
        item.children.forEach(c => out.push({ path: c.path, title: c.title }));
      }
      return;
    }

    // 页面项：可能是模块的扁平副本，也可能是独立页面（如 /dashboard）
    const owner = ownerOf(item.path);
    if (owner) {
      // 模块已整体选中 -> 已随模块输出，跳过；否则视自身是否被选中
      if (picked.indexOf(owner) !== -1) return;
    }
    if (picked.indexOf(item.path) !== -1) {
      out.push({ path: item.path, title: item.title });
    }
  });

  return out;
}

/**
 * permission（完整树）-> 勾选路径集合，用于回显 el-tree
 *
 * 取顶层的「模块节点」与「独立页面」：
 *   带 children 的顶层项        => 模块，其 path 即勾选值
 *   不带 children 的顶层项      => 若其祖先模块已在顶层出现，则是扁平副本，跳过；
 *                                 否则是独立页面（或单独勾选的页面），保留
 */
export function parsePermission(permission) {
  const valid = allPaths();
  const items = (permission || []).filter(it => it && typeof it.path === 'string');
  const modules = items.filter(it => Array.isArray(it.children) && it.children.length)
    .map(it => it.path);

  const keys = items
    .map(it => it.path)
    .filter(p => valid.indexOf(p) !== -1)
    .filter((p, i, arr) => arr.indexOf(p) === i)                    // 去重
    .filter(p => modules.indexOf(p) !== -1 ||                        // 模块
      !ancestorsOf(p).some(a => modules.indexOf(a) !== -1));         // 非副本

  // 父级覆盖子级：有模块就不要其下页面
  const set = new Set(keys.filter(p => !ancestorsOf(p).some(a => keys.indexOf(a) !== -1)));

  // 按树顺序输出，保证回显结果稳定（与勾选顺序无关）
  const ordered = [];
  const walk = nodes => (nodes || []).forEach(n => {
    if (set.has(n.path)) {
      ordered.push(n.path);
      return;
    }
    if (n.children && n.children.length) walk(n.children);
  });
  walk(toTree());
  return ordered;
}

/** 展开成叶子页面路径集合，用于下发给业务系统做访问控制 */
export function expandToLeaves(permission) {
  const keys = parsePermission(permission);
  const out = [];
  const walk = nodes => (nodes || []).forEach(n => {
    if (n.children && n.children.length) walk(n.children);
    else if (keys.some(p => n.path === p || n.path.indexOf(p + '/') === 0)) out.push(n.path);
  });
  walk(toTree());
  return out;
}

/** 权限覆盖的页面总数 */
export function countPages(permission) {
  return expandToLeaves(permission).length;
}

/**
 * 权限摘要（表格 / 已选区展示用）
 * 只列出「模块」与「实际勾选的页面」，不含扁平副本
 * 返回 [{ path, title, count }]，count = 模块下页面数（页面为 0）
 */
export function summarize(permission) {
  const keys = parsePermission(permission);
  const out = [];
  const walk = nodes => (nodes || []).forEach(n => {
    const isModule = !!(n.children && n.children.length);
    if (keys.indexOf(n.path) !== -1) {
      out.push({
        path: n.path,
        title: n.title,
        count: isModule ? n.children.length : 0
      });
      return;
    }
    if (isModule) walk(n.children);
  });
  walk(toTree());
  return out;
}

/** 摘要项展示文案：模块「角色模块（全部 20 项）」，页面直接标题 */
export function describeItem(item) {
  if (!item) return '';
  if (item.count) return `${item.title}（全部 ${item.count} 项）`;
  return item.title;
}

/**
 * 判断某页面是否在权限内（父级命中即算有权限）
 * 例：permission 含 /role 模块 → /role/roleInfo 通过
 */
export function hasPermission(permission, path) {
  if (!path) return false;
  const keys = parsePermission(permission);
  if (keys.indexOf(path) !== -1) return true;
  return ancestorsOf(path).some(a => keys.indexOf(a) !== -1);
}
